import { Mic, MicOff, Play, Pause, RotateCcw, Send } from "lucide-react";
import React, { useState, useRef, useEffect } from "react";

import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { useToast } from "@/app/_module/hooks/use-toast";

import type { VoicemailRecord } from "../types/voicemail";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/app/_module/components/ui/dialog";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { Textarea } from "@/app/_module/components/ui/textarea";

interface Lead {
  id: string;
  phone?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

interface LeaveVoicemailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead?: Lead; // Make lead optional to allow selecting from list
  leads?: Lead[]; // Add leads list for selection
  onSend?: (voicemail: VoicemailRecord) => void;
}

type RecordingState = "idle" | "recording" | "recorded" | "playing";

export const LeaveVoicemailModal: React.FC<LeaveVoicemailModalProps> = function({
  open,
  onOpenChange,
  lead: initialLead,
  leads = [],
  onSend,
}) {
  const { toast } = useToast();
  const [selectedLeadId, setSelectedLeadId] = useState<string>(initialLead?.id || "");
  const [phone, setPhone] = useState<string>(initialLead?.phone || "");
  const [message, setMessage] = useState<string>("");
  const [priority, setPriority] = useState<"low" | "normal" | "high">("normal");
  const [loading, setLoading] = useState(false);
  const [recordingState, setRecordingState] = useState<RecordingState>("idle");
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);

  // Get the currently selected lead
  const selectedLead = leads.find(l => l.id === selectedLeadId) || initialLead;

  // Update phone and lead when initial lead changes
  useEffect(() => {
    if (initialLead) {
      setSelectedLeadId(initialLead.id);
      setPhone(initialLead.phone || "");
    }
  }, [initialLead]);

  // Update phone when selected lead changes
  useEffect(() => {
    if (selectedLead?.phone) {
      setPhone(selectedLead.phone);
    }
  }, [selectedLead]);

  // Timer effect for recording
  useEffect(() => {
    if (recordingState === "recording" && startTimeRef.current) {
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current!) / 1000);
        setRecordingDuration(elapsed);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [recordingState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (mediaRecorderRef.current && recordingState === "recording") {
        mediaRecorderRef.current.stop();
      }
    };
  }, [recordingState]);

  const startRecording = async () => {
    try {
      // Request microphone permission with proper cross-browser support
      const { requestMicrophoneWithGuidance } = await import("@/app/_module/utils/microphone-permission");
      const permissionResult = await requestMicrophoneWithGuidance();

      if (!permissionResult.granted) {
        toast({
          title: "Microphone Access Required",
          description: permissionResult.error || "Please allow microphone access to record voicemail.",
          variant: "destructive",
        });
        return;
      }

      // Now request the actual stream for recording
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });

      const mediaRecorder = new MediaRecorder(stream);
      const audioChunks: BlobPart[] = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: "audio/wav" });
        setAudioBlob(audioBlob);
        setRecordingState("recorded");

        // Stop all tracks to turn off the microphone
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();

      // Set start time and reset duration
      startTimeRef.current = Date.now();
      setRecordingDuration(0);
      setRecordingState("recording");

    } catch (error) {

      toast({
        title: "Recording Failed",
        description: "Unable to access microphone. Please check your permissions and try again.",
        variant: "destructive",
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recordingState === "recording") {
      mediaRecorderRef.current.stop();
      
      // Calculate final duration
      if (startTimeRef.current) {
        const finalDuration = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setRecordingDuration(finalDuration);
      }
    }
  };

  const playRecording = () => {
    if (audioBlob && audioRef.current) {
      const audioUrl = URL.createObjectURL(audioBlob);
      audioRef.current.src = audioUrl;
      audioRef.current.play().catch(() => {});
      setRecordingState("playing");
      
      audioRef.current.onended = () => {
        setRecordingState("recorded");
        URL.revokeObjectURL(audioUrl);
      };
    }
  };

  const pauseRecording = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setRecordingState("recorded");
    }
  };

  const resetRecording = () => {
    setRecordingState("idle");
    setRecordingDuration(0);
    setAudioBlob(null);
    startTimeRef.current = null;
    if (audioRef.current) {
      audioRef.current.src = "";
    }
  };

  const handleSend = async () => {
    if (!phone || (!audioBlob && !message)) return;

    setLoading(true);
    try {
      // Real API call to send voicemail
      // Backend expects: { to: string, message: string, file?: string }
      const leadToUse = selectedLead || initialLead;

      // If we have an audio blob, we need to upload it first to get a URL
      let audioFileUrl: string | undefined;
      if (audioBlob) {
        // Upload the audio file first
        const uploadFormData = new FormData();
        uploadFormData.append("file", audioBlob, "voicemail.wav");
        if (leadToUse?.id) {
          uploadFormData.append("leadId", leadToUse.id);
        }

        try {
          const uploadResponse = await api.post({
            url: getApiUrl("/communication/voicemail/upload"),
            body: uploadFormData,
            headers: {
              'Content-Type': 'multipart/form-data',
            }
          });

          if (uploadResponse.data && (uploadResponse.data as any).data?.audioUrl) {
            audioFileUrl = (uploadResponse.data as any).data.audioUrl;
          } else {
            toast({
              title: "Upload Failed",
              description: "Failed to upload the recording. Please try again.",
              variant: "destructive",
            });
            setLoading(false);
            return;
          }
        } catch (uploadError) {
          toast({
            title: "Upload Failed",
            description: "Failed to upload the recording. Please try again.",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
      }

      // Send the voicemail with correct field names
      const response = await api.post({
        url: getApiUrl("/communication/voicemail/send"),
        body: {
          to: phone,           // Backend expects "to", not "phone"
          message: message || "You have a voicemail message.",
          file: audioFileUrl,  // Backend expects "file", not "audio"
          leadId: leadToUse?.id,
          priority,
        },
      });

      if (response.data && (response.data as any).statusCode === 200) {
        // Create new voicemail record from API response
        const responseData = (response.data as any).data;
        const leadToUse = selectedLead || initialLead;
        const newVoicemail = {
          id: responseData?.id || `vm-${Date.now()}`,
          leadId: leadToUse?.id || '',
          phoneNumber: phone,
          contactName: leadToUse?.firstName && leadToUse?.lastName ? `${leadToUse.firstName} ${leadToUse.lastName}` : undefined,
          direction: "outbound" as const,
          duration: recordingDuration > 0 ? recordingDuration : 30,
          audioUrl: responseData?.audioUrl || `voicemail-${Date.now()}.wav`,
          transcript: message || (audioBlob ? "Recorded voice message" : "Text message sent as voicemail"),
          isListened: true,
          timestamp: new Date(),
          priority,
          notes: message || undefined,
        };

        // Call the onSend callback to update parent state
        onSend?.(newVoicemail);

        // Reset form and close modal
        setMessage("");
        setPriority("normal");
        resetRecording();
        onOpenChange(false);
        
        toast({
          title: "Voicemail Sent",
          description: "Voicemail sent successfully! Check the Sent tab to see your message.",
        });
      } else {
        throw new Error('Failed to send voicemail');
      }
      
    } catch (error) {

      toast({
        title: "Error",
        description: "Failed to send voicemail. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const canSend = phone && (audioBlob || message.trim()) && !loading;

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <ResponsiveDialogContent size="md">
        <DialogHeader>
          <DialogTitle>Leave Voicemail</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Lead Selector - only show if we have multiple leads to choose from */}
          {leads.length > 0 && (
            <div>
              <Label htmlFor="lead">Select Lead</Label>
              <Select
                onValueChange={(value: string) => setSelectedLeadId(value)}
                value={selectedLeadId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a lead" />
                </SelectTrigger>
                <SelectContent>
                  {leads.map((lead) => (
                    <SelectItem key={lead.id} value={lead.id}>
                      {lead.firstName && lead.lastName
                        ? `${lead.firstName} ${lead.lastName}`
                        : lead.phone || 'Unknown Lead'}
                      {lead.phone && ` - ${lead.phone}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              required
              id="phone"
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Recipient phone number"
              value={phone}
            />
            {selectedLead && (
              <p className="text-xs text-muted-foreground mt-1">
                Sending to: {selectedLead.firstName && selectedLead.lastName
                  ? `${selectedLead.firstName} ${selectedLead.lastName}`
                  : 'Selected Lead'}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="priority">Priority</Label>
            <Select onValueChange={(value: "low" | "normal" | "high") => setPriority(value)} value={priority}>
              <SelectTrigger>
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Audio Recording Section */}
          <div className="space-y-3">
            <Label>Voice Recording</Label>
            <div className="border rounded-lg p-4 space-y-3">
              {recordingState === "idle" && (
                <div className="text-center">
                  <Button
                    className="w-full"
                    onClick={startRecording}
                    type="button"
                    variant="outline"
                  >
                    <Mic className="h-4 w-4 mr-2" />
                    Start Recording
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    Click to record your voicemail message
                  </p>
                </div>
              )}

              {recordingState === "recording" && (
                <div className="text-center space-y-2">
                  <Badge className="mb-2" variant="destructive">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse" />
                    Recording... {formatTime(recordingDuration)}
                  </Badge>
                  <Button
                    className="w-full"
                    onClick={stopRecording}
                    type="button"
                    variant="outline"
                  >
                    <MicOff className="h-4 w-4 mr-2" />
                    Stop Recording
                  </Button>
                </div>
              )}

              {recordingState === "recorded" && (
                <div className="space-y-2">
                  <Badge className="mb-2" variant="secondary">
                    Recorded: {formatTime(recordingDuration)}
                  </Badge>
                  <div className="flex gap-2">
                    <Button
                      className="flex-1"
                      onClick={playRecording}
                      size="sm"
                      type="button"
                      variant="outline"
                    >
                      <Play className="h-4 w-4 mr-1" />
                      Play
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={resetRecording}
                      size="sm"
                      type="button"
                      variant="outline"
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Re-record
                    </Button>
                  </div>
                </div>
              )}

              {recordingState === "playing" && (
                <div className="space-y-2">
                  <Badge className="mb-2" variant="secondary">
                    Playing... {formatTime(recordingDuration)}
                  </Badge>
                  <Button
                    className="w-full"
                    onClick={pauseRecording}
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    <Pause className="h-4 w-4 mr-2" />
                    Pause
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="message">Additional Message (Optional)</Label>
            <Textarea
              id="message"
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add any additional notes or context..."
              rows={3}
              value={message}
            />
            <p className="text-xs text-muted-foreground mt-1">
              This message will be included with your voicemail
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            className="w-full bg-blue-600 hover:bg-blue-700"
            disabled={!canSend}
            onClick={handleSend}
          >
            {loading ? (
              <React.Fragment>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Sending...
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Send className="h-4 w-4 mr-2" />
                Send Voicemail
              </React.Fragment>
            )}
          </Button>
        </DialogFooter>

        <audio ref={audioRef} hidden />
      </ResponsiveDialogContent>
    </Dialog>
  );
}
